import React, { useEffect } from 'react';
import { AlertCircle, CheckSquare } from 'lucide-react';

// Sample chapter shown to Pranay so he can approve the format before we write the
// other 22. Gaps marked in amber are the judgment layer only he can fill.

const Gap = ({ children }) => (
    <div className="my-8 rounded-2xl border border-amber-500/30 bg-amber-900/10 p-5 sm:p-6">
        <div className="flex items-center gap-2 text-amber-300/90 text-[10px] font-bold tracking-[0.2em] uppercase mb-3">
            <AlertCircle className="w-4 h-4" /> Your bit
        </div>
        <p className="text-sm sm:text-base text-amber-50/80 font-light leading-relaxed">{children}</p>
    </div>
);

const H2 = ({ children }) => (
    <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight mt-14 mb-5">{children}</h2>
);

const P = ({ children }) => (
    <p className="text-base sm:text-lg text-white/70 font-light leading-relaxed mb-5">{children}</p>
);

const Cite = ({ children }) => (
    <p className="text-xs sm:text-sm text-white/35 font-light leading-relaxed mb-8 italic">{children}</p>
);

export default function PranaySampleChapter() {
    useEffect(() => {
        document.title = "Sample Chapter | Pranay Jham";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-32 sm:pt-36 pb-20 relative z-10">

            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Pranay Jham</span>
                </div>
                <div className="bg-amber-500/20 backdrop-blur-lg border border-amber-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-amber-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Sample</span>
                </div>
            </header>

            <main className="max-w-[720px] mx-auto px-6">

                <div className="text-center mb-14">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-6">
                        One chapter, so you can see the shape
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.08] pb-2">
                        The five numbers that decide if your product can make money.
                    </h1>
                    <p className="text-sm sm:text-base text-white/45 font-light leading-relaxed">
                        Chapter 1 of 23. This is a draft, not the finished thing. Tell us what is wrong and what you would add, and we write the other 22 this way.
                    </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 mb-14">
                    <p className="text-sm text-white/60 font-light leading-relaxed">
                        <span className="text-white font-bold">How to read this.</span> The blue parts are ours, researched and sourced. The amber boxes are yours, the judgment only you can supply. Every number below comes from company filings or a named report, and you will find the source printed under each one.
                    </p>
                </div>

                <hr className="border-white/10 mb-12" />

                <P>
                    <span className="text-white font-bold">You can sell a product with a 70% margin and still lose money on every order.</span> Most founders find this out at order 200, when the bank balance does not match the sales screen. This chapter is how you find out before you spend anything.
                </P>

                <Gap>
                    Your opening story. The moment you realised the margin number was lying to you, or the first time you worked out what an order really earned you. With the numbers.
                </Gap>

                <H2>Why the margin on your product is not the margin on your order</H2>
                <P>
                    Ask a founder their margin and they tell you one number. Product cost against selling price. That number ignores the box, the courier, the payment fee, the returns, and the money you spent to find the customer.
                </P>
                <P>
                    So stop measuring at the product. Measure at the order, then at the customer, then at the cash in your bank. That is five numbers, in this order.
                </P>

                <H2>The five numbers</H2>
                <P>
                    <span className="text-white font-bold">1. Margin on the unit.</span> Selling price, minus tax, minus what the product actually costs you landed. Landed means manufacturing, the packaging on the product, and freight to your warehouse. Nothing else goes here.
                </P>
                <P>
                    <span className="text-white font-bold">2. Margin on the order, before you advertise.</span> Take number 1. Now subtract the shipping box, the courier, the payment gateway fee, the money you set aside for returns, and the cost of picking and packing. This is the first honest number. It tells you whether the order was worth sending.
                </P>
                <P>
                    <span className="text-white font-bold">3. Margin on the order, after you advertise.</span> Take number 2 and subtract what you paid to get that customer. If this is negative, every new customer makes you poorer. Volume will not save you.
                </P>
                <P>
                    <span className="text-white font-bold">4. How many orders a month you need to break even.</span> Your fixed monthly costs divided by number 2. If number 2 is negative, this number does not exist. No amount of volume fixes a broken order.
                </P>
                <P>
                    <span className="text-white font-bold">5. How long your money is stuck.</span> Days your stock sits, plus days until the payment reaches your bank, minus any credit your supplier gives you. This is the one that kills healthy brands. Your customer paid. Your bank account has not.
                </P>

                <Gap>
                    Which of these did you actually track in your first hundred orders, and which one do you wish you had tracked from day one? Would you swap any of them for repeat rate?
                </Gap>

                <H2>What real Indian brands actually earn</H2>
                <P>These come from company filings, not from an agency's model.</P>

                <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/15">
                                <th className="text-left py-3 pr-4 text-white/50 font-bold text-xs uppercase tracking-wider">Brand</th>
                                <th className="text-left py-3 pr-4 text-white/50 font-bold text-xs uppercase tracking-wider">Gross margin</th>
                                <th className="text-left py-3 text-white/50 font-bold text-xs uppercase tracking-wider">That year</th>
                            </tr>
                        </thead>
                        <tbody className="text-white/70">
                            <tr className="border-b border-white/5"><td className="py-3 pr-4 font-bold text-white">Minimalist</td><td className="py-3 pr-4">71%</td><td className="py-3">₹514.8 Cr revenue, ₹18 Cr EBITDA</td></tr>
                            <tr className="border-b border-white/5"><td className="py-3 pr-4 font-bold text-white">Lenskart</td><td className="py-3 pr-4">70%</td><td className="py-3">₹6,652 Cr revenue, ₹297 Cr profit</td></tr>
                            <tr className="border-b border-white/5"><td className="py-3 pr-4 font-bold text-white">boAt</td><td className="py-3 pr-4">38 to 42%</td><td className="py-3">₹3,090 Cr revenue, ₹60 Cr profit</td></tr>
                            <tr><td className="py-3 pr-4 font-bold text-white">Licious</td><td className="py-3 pr-4">30%</td><td className="py-3">₹797 Cr revenue, ₹218 Cr loss</td></tr>
                        </tbody>
                    </table>
                </div>
                <Cite>Filing-derived, FY25. The CPG Lab study of Indian D2C brands, from RoC, BSE and DRHP filings, 2026.</Cite>

                <P>
                    Read that table again. Licious is a bigger, better known company than Minimalist and it lost ₹218 crore. Minimalist made money. The difference is not effort. It is the category they chose, which set the ceiling before either of them did anything.
                </P>
                <P>
                    <span className="text-white font-bold">So your first real decision is not your brand name. It is whether your category can carry a margin you can survive on.</span>
                </P>
                <P>
                    One study of 34 Indian D2C brands puts the line at 60% gross margin to survive and 70% to win, and found gross margin the strongest single predictor of a brand reaching profit. That is the author's threshold, not a law, but the filings above sit either side of it in the way you would expect.
                </P>

                <H2>Two numbers that catch people out</H2>
                <P>
                    <span className="text-white font-bold">Advertising.</span> Indian D2C brands in early growth spend 30 to 40% of revenue on digital ads. Build that into number 3 before you launch, not after.
                </P>
                <P>
                    <span className="text-white font-bold">Repeat buyers.</span> Across Indian ecommerce, 84 out of every 100 buyers are buying for the first time. The survival line is reported at a 35 to 40% repeat rate. So assume the first sale has to pay for itself, and treat the second one as a bonus until your own data says otherwise.
                </P>
                <Cite>Forbes India, 2025. Shiprocket Trends live data, read September 2026.</Cite>

                <Gap>
                    You built on under 10% marketing spend. Forbes says 30 to 40% is normal. Who is wrong, and what were you doing instead?
                </Gap>

                <H2>Now do it with your own product</H2>
                <P>Write these down. Actual numbers, not estimates you feel good about.</P>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 mb-6">
                    <ul className="space-y-2 text-sm sm:text-base text-white/65 font-light">
                        <li>1. Your selling price, minus tax</li>
                        <li>2. What one unit costs you, landed</li>
                        <li>3. Box, courier, payment fee, returns provision, pick and pack</li>
                        <li>4. What you expect to pay to get one customer</li>
                        <li>5. Your fixed costs each month</li>
                    </ul>
                </div>
                <P>
                    Then: 1 minus 2 is your unit margin. That minus 3 is your order margin. That minus 4 is what you actually keep. Your fixed costs divided by your order margin is how many orders a month you need before you earn a rupee.
                </P>
                <p className="text-base sm:text-lg text-white/45 font-light leading-relaxed mb-5">The calculator sheet does this for you.</p>

                <H2>Do this today</H2>
                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-900/10 p-5 sm:p-6 mb-8">
                    <ul className="space-y-3">
                        {[
                            'Work out what one unit costs you landed, including inbound freight',
                            'Price one order end to end, including the return you will get',
                            'Find your category and see if your margin clears it',
                            'If your order margin is negative, fix the price or the product cost before spending a rupee on ads',
                            'Write down how many days your money will be stuck',
                        ].map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-white/70 font-light leading-relaxed">
                                <CheckSquare className="w-4 h-4 text-emerald-400 shrink-0 mt-1" />
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                <Gap>
                    What would you add here that nobody else writes?
                </Gap>

                <hr className="border-white/10 my-14" />

                <div className="rounded-2xl border border-indigo-500/25 bg-indigo-900/10 p-6 sm:p-8 text-center">
                    <div className="text-indigo-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">What we need back</div>
                    <p className="text-sm sm:text-base text-white/65 font-light leading-relaxed">
                        Tell us three things. What is wrong or out of date. What you would add that nobody else writes. And what you actually did differently. Voice notes are perfect, no need to type. If this shape works for you, we write the other 22 exactly like this.
                    </p>
                </div>

                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-16 border-t border-white/5 mt-14">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
